import { PathHelper } from '@sensenet/client-utils';
import { BypassAuthentication } from "../Authentication/BypassAuthentication.js";
import { SchemaStore } from "../Schemas/SchemaStore.js";
import { AllowedChildTypes } from "./AllowedChildTypes.js";
import { ConstantContent } from "./ConstantContent.js";
import { ODataUrlBuilder } from "./ODataUrlBuilder.js";
import { Preview } from "./Preview.js";
import { defaultRepositoryConfiguration } from "./RepositoryConfiguration.js";
import { Security } from "./Security.js";
import { Upload } from "./Upload.js";
import { Versioning } from "./Versioning.js";
/**
 * Defines an extended error message instance that contains an original error instance, a response and a parsed JSON body from the response
 */
/**
 * Type guard to check if an error is extended with a response and a parsed body
 * @param e The error to check
 */
export const isExtendedError = e => {
  return e.response ? true : false;
};

/**
 * Class that can be used as a main entry point to manipulate a sensenet content repository
 */
export class Repository {
  /**
   * Disposes the Repository object
   */
  dispose() {
    this.authentication.dispose();
  }

  /**
   * Authentication service associated with the repository object
   */

  /**
   * The configuration for the Repository object
   */

  /**
   * Async method that will be resolved when the Repository is ready to make HTTP calls
   */
  async awaitReadyState() {
    await Promise.all([this.authentication.checkForUpdate()]);
  }

  /**
   * Wrapper for a native window.fetch method. The repository's readyState will be awaited and credentials will be included by default
   * @param {RequestInfo} input The RequestInfo object
   * @param {RequestInit} init Optional init parameters
   */
  async fetch(input, init, awaitReadyState = true) {
    if (awaitReadyState) {
      await this.awaitReadyState();
    }
    const request = new Request(input, init);
    if (this.configuration.token) {
      request.headers.append('Authorization', `Bearer ${this.configuration.token}`);
    }
    return await this.fetchMethod(request);
  }

  /**
   * Gets a more meaningful error object from a specific response
   * @param response The Response object to extract the message
   */
  async getErrorFromResponse(response) {
    let msgFromBody = '';
    let body = {};
    let text = '';
    try {
      body = await response.json();
      msgFromBody = body.error.message.value;
    } catch (error) {
      /** */
    }
    try {
      text = await response.text();
    } catch (error) {
      /** */
    }
    const error = new Error(msgFromBody || text || response.statusText);
    error.body = body;
    error.response = response;
    error.text = text;
    error.statusCode = response.status;
    error.statusText = response.statusText;
    error.url = response.url;
    return error;
  }

  /**
   * Loads a content from the content repository. If used with a fully qualified content path,
   * it will be transformed to an item path.
   * @param options Options for the Load request
   */
  async load(options) {
    const contentPath = PathHelper.getContentUrl(options.idOrPath);
    const params = ODataUrlBuilder.buildUrlParamString(this.configuration, options.oDataOptions);
    const path = PathHelper.joinPaths(this.configuration.repositoryUrl, this.configuration.oDataToken, contentPath);
    const response = await this.fetch(`${path}?${params}`, {
      ...options.requestInit,
      credentials: 'include',
      method: 'GET'
    });
    if (!response.ok) {
      throw await this.getErrorFromResponse(response);
    }
    return await response.json();
  }

  /**
   * Loads a content collection from the repository
   * @param options Options for the Load request
   */
  async loadCollection(options) {
    const params = ODataUrlBuilder.buildUrlParamString(this.configuration, options.oDataOptions);
    const path = PathHelper.joinPaths(this.configuration.repositoryUrl, this.configuration.oDataToken, options.path);
    const response = await this.fetch(`${path}?${params}`, {
      ...options.requestInit,
      credentials: 'include',
      method: 'GET'
    });
    if (!response.ok) {
      throw await this.getErrorFromResponse(response);
    }
    return await response.json();
  }

  /**
   * Returns the count of items in the requested collection.
   * The value depends on other optional query string parameters ($top, $skip, $filter, query, etc.) and does not depend on the $inlinecount parameter.
   */
  async count(options) {
    const params = ODataUrlBuilder.buildUrlParamString(this.configuration, options.oDataOptions);
    const path = PathHelper.joinPaths(this.configuration.repositoryUrl, this.configuration.oDataToken, options.path);
    const response = await this.fetch(`${path}/$count?${params}`, {
      ...options.requestInit,
      credentials: 'include',
      method: 'GET'
    });
    if (!response.ok) {
      throw await this.getErrorFromResponse(response);
    }
    return await response.json();
  }

  /**
   * Posts a new content to the content repository
   * @param options Post request Options
   */
  async post(options) {
    const path = PathHelper.joinPaths(this.configuration.repositoryUrl, this.configuration.oDataToken, options.parentPath);
    const params = ODataUrlBuilder.buildUrlParamString(this.configuration, options.oDataOptions);
    const postBody = Object.assign({}, options.content);
    postBody.__ContentType = options.contentType;
    postBody.__ContentTemplate = options.contentTemplate;
    const response = await this.fetch(`${path}?${params}`, {
      ...options.requestInit,
      credentials: 'include',
      method: 'POST',
      body: JSON.stringify(postBody)
    });
    if (!response.ok) {
      throw await this.getErrorFromResponse(response);
    }
    if (response.status === 204) {
      return Promise.resolve({
        d: null
      });
    }
    return await response.json();
  }

  /**
   * Updates an existing content in the repository using OData Patch
   * @param options Options for the Patch request
   */
  async patch(options) {
    const contentPath = PathHelper.getContentUrl(options.idOrPath);
    const path = PathHelper.joinPaths(this.configuration.repositoryUrl, this.configuration.oDataToken, contentPath);
    const params = ODataUrlBuilder.buildUrlParamString(this.configuration, options.oDataOptions);
    const response = await this.fetch(`${path}?${params}`, {
      ...options.requestInit,
      credentials: 'include',
      method: 'PATCH',
      body: JSON.stringify(options.content)
    });
    if (!response.ok) {
      throw await this.getErrorFromResponse(response);
    }
    return await response.json();
  }

  /**
   * Updates an existing content in the repository using OData Put
   * @param options Options for the Put request
   */
  async put(options) {
    const contentPath = PathHelper.getContentUrl(options.idOrPath);
    const path = PathHelper.joinPaths(this.configuration.repositoryUrl, this.configuration.oDataToken, contentPath);
    const params = ODataUrlBuilder.buildUrlParamString(this.configuration, options.oDataOptions);
    const response = await this.fetch(`${path}?${params}`, {
      ...options.requestInit,
      credentials: 'include',
      method: 'PUT',
      body: JSON.stringify(options.content)
    });
    if (!response.ok) {
      throw await this.getErrorFromResponse(response);
    }
    return await response.json();
  }
  createArray(param) {
    if (!(param instanceof Array)) {
      return [param];
    }
    return param;
  }

  /**
   * Deletes a content or a content collection from the Repository
   * @param options Options for the Delete request
   */
  async delete(options) {
    var _options$permanent;
    return await this.executeAction({
      idOrPath: ConstantContent.PORTAL_ROOT.Path,
      method: 'POST',
      name: 'DeleteBatch',
      requestInit: options.requestInit,
      body: {
        paths: this.createArray(options.idOrPath),
        permanent: (_options$permanent = options.permanent) !== null && _options$permanent !== void 0 ? _options$permanent : false
      }
    });
  }

  /**
   * Moves a content or content collection to a specified location
   * @param options Options for the Move request
   */
  async move(options) {
    return await this.executeAction({
      idOrPath: ConstantContent.PORTAL_ROOT.Path,
      method: 'POST',
      name: 'MoveBatch',
      requestInit: options.requestInit,
      body: {
        paths: this.createArray(options.idOrPath),
        targetPath: options.targetPath
      }
    });
  }

  /**
   * Copies a content or content collection to a specified location
   * @param options Options for the Copy request
   */
  async copy(options) {
    return await this.executeAction({
      idOrPath: ConstantContent.PORTAL_ROOT.Path,
      method: 'POST',
      name: 'CopyBatch',
      requestInit: options.requestInit,
      body: {
        paths: this.createArray(options.idOrPath),
        targetPath: options.targetPath
      }
    });
  }
  async getPropertyValue(idOrPath, propertyName) {
    const path = PathHelper.joinPaths(this.configuration.repositoryUrl, this.configuration.oDataToken, PathHelper.getContentUrl(idOrPath), propertyName, '$value');
    const response = await this.fetch(path);
    if (!response.ok) {
      throw await this.getErrorFromResponse(response);
    }
    return await response.text();
  }
  async getMetadata(idOrPath) {
    const path = idOrPath === undefined ? PathHelper.joinPaths(this.configuration.repositoryUrl, this.configuration.oDataToken, '$metadata') : PathHelper.joinPaths(this.configuration.repositoryUrl, this.configuration.oDataToken, PathHelper.getContentUrl(idOrPath), '$metadata');
    const response = await this.fetch(`${path}?$format=json`);
    if (!response.ok) {
      throw await this.getErrorFromResponse(response);
    }
    return await response.json();
  }

  /**
   * Shares a content or content collection with a specified
   * @param options Options for the Copy request
   */
  async share(options) {
    var _options$sendNotifica;
    return await this.executeAction({
      idOrPath: options.content.Id,
      method: 'POST',
      name: 'Share',
      body: {
        token: options.identity.toString(),
        content: options.content,
        level: options.sharingLevel,
        mode: options.sharingMode,
        sendNotification: (_options$sendNotifica = options.sendNotification) !== null && _options$sendNotifica !== void 0 ? _options$sendNotifica : true
      }
    });
  }

  /**
   * Retrieves a list of content actions for a specified content
   * @param options Options for fetching the Custom Actions
   */
  async getActions(options) {
    const contextPath = PathHelper.getContentUrl(options.idOrPath);
    const path = PathHelper.joinPaths(this.configuration.repositoryUrl, this.configuration.oDataToken, contextPath, 'Actions');
    const response = await this.fetch(`${path}${options.scenario ? `?scenario=${options.scenario}` : ''}`, {
      credentials: 'include',
      method: 'GET'
    });
    if (!response.ok) {
      throw await this.getErrorFromResponse(response);
    }
    return await response.json();
  }

  /**
   * Returns data for loading Office document for editing
   * @param idOrPath Id or path of the document
   */
  async getWopiData(options) {
    return await this.executeAction({
      idOrPath: options.idOrPath,
      method: 'GET',
      name: 'GetWopiData',
      requestInit: options.requestInit,
      oDataOptions: {
        ...options.odataOptions,
        action: options.action || 'edit'
      }
    });
  }

  /**
   * Executes a specified custom OData action
   * @param options Options for the Custom Action
   */
  async executeAction(options) {
    const contextPath = PathHelper.getContentUrl(options.idOrPath);
    let params = ODataUrlBuilder.buildUrlParamString(this.configuration, options.oDataOptions);
    const path = PathHelper.joinPaths(this.configuration.repositoryUrl, this.configuration.oDataToken, contextPath, options.name);
    const requestOptions = {
      ...options.requestInit,
      credentials: 'include',
      method: options.method
    };
    if (options.method === 'POST') {
      requestOptions.body = JSON.stringify(options.body);
    } else {
      options.body && Object.keys(options.body).forEach(key => params += `&${key}=${encodeURIComponent(options.body[key])}`);
    }
    const response = await this.fetch(`${path}?${params}`, requestOptions);
    if (!response.ok) {
      throw await this.getErrorFromResponse(response);
    }
    if (response.status === 204) {
      return Promise.resolve({});
    }
    return await response.json();
  }

  /**
   * Shortcut for security- and permission-related custom actions
   */

  /**
   * Shortcut for versioning related custom actions
   */

  /**
   * Shortcut for upload related custom actions
   */

  /**
   * Shortcut for preview related custom actions
   */

  /**
   * Shortcut for allowedChildTypes related custom actions
   */

  /**
   * Reloads the content schemas from the sensenet backend
   * @returns {Promise<void>} A promise that will be resolved / rejected based on the action success
   */
  async reloadSchema() {
    const schemas = await this.executeAction({
      idOrPath: 'Root',
      name: 'GetSchema',
      method: 'GET',
      body: undefined
    });
    this.schemas.setSchemas(schemas);
  }
  constructor(config, fetchMethod = window && window.fetch && window.fetch.bind(window), schemas = new SchemaStore()) {
    this.fetchMethod = fetchMethod;
    this.schemas = schemas;
    this.authentication = new BypassAuthentication();
    this.configuration = void 0;
    this.security = new Security(this);
    this.versioning = new Versioning(this);
    this.upload = new Upload(this);
    this.preview = new Preview(this);
    this.allowedChildTypes = new AllowedChildTypes(this);
    this.iconCache = new Map();
    this.configuration = {
      ...defaultRepositoryConfiguration,
      ...config
    };
    this.schemas.setSchemas(this.configuration.schemas);
  }
}
//# sourceMappingURL=Repository.js.map